<script setup>
import '@campact/camper/core/CmprButton';
import '@campact/camper/core/CmprFormHint';
import '@campact/camper/cta/CmprPrefilledArea';
import '@campact/camper/icons/CmprIconSolidCheck';

import { computed, inject, ref } from 'vue';
import { inputToCents, localizeAmount } from '@cta/frontend/javascripts/money';
import StepItem from './StepItem.vue';
import CmprInput from '@cta/frontend/components/CmprInput.vue';
import RecurringDonationOffer from './RecurringDonationOffer.vue';

const store = inject('store');

const props = defineProps({
  amountChoices: {
    type: Array,
    default() {
      return [];
    },
  },
  stepTitles: {
    type: Object,
    default() {
      return {
        valid: 'Betrag ausgewählt',
        invalid: 'Betrag wählen',
      };
    },
  },
  summary: {
    type: Function,
    default: (amount) => localizeAmount(amount),
  },
  analyticsIdPrefix: {
    type: String,
    default: 'donate-call',
  },
  recurringDonationOfferCheckboxLabel: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferEnabled: {
    type: Boolean,
    default: false,
  },
  recurringDonationOfferHintPlacement: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferHintText: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferOnetimeButtonLabel: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferOnetimeButtonPreselected: {
    type: Boolean,
    default: false,
  },
  recurringDonationOfferRecurringButtonLabel: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferRequestText: {
    type: String,
    default: undefined,
  },
  recurringDonationOfferUiElement: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(['try-open', 'validate', 'commit']);

const amountChoice = ref(undefined);
const amountInput = ref(store.values.amount ? localizeAmount(store.values.amount) : undefined);
const amountCommitButtonVisible = ref(false);
const amountStepTitle = computed(() => {
  if (store.values.amount !== undefined && store.amountStepValid) return props.stepTitles.valid;
  return props.stepTitles.invalid;
});

function amountChoiceVariant(index) {
  if (store.values.amount) return 'secondary';

  return index === Math.floor(props.amountChoices.length / 2) ? 'primary' : 'secondary';
}

function chooseAmount(newChoice) {
  amountChoice.value = newChoice;
  amountInput.value = '';
  store.setAmount(newChoice);

  // If the offer to switch to a recurring donation is enabled, clicking one of the amount buttons should not
  // immediately take the user to the next step.
  if (props.recurringDonationOfferEnabled) {
    // If the segmented control is used and the one-time option is not preselected, the user has to click one of the
    // two buttons. This click "commits" the step, so the 'Continue' doesn't have to be shown.
    if (props.recurringDonationOfferUiElement === 'segmented_control'
      && !props.recurringDonationOfferOnetimeButtonPreselected) {
      amountCommitButtonVisible.value = false;
    } else {
      amountCommitButtonVisible.value = true;
    }

    emit('validate');
  } else {
    amountCommitButtonVisible.value = false;
    emit('commit');
  }
}

function inputAmount() {
  amountChoice.value = undefined;
  store.setAmount(inputToCents(amountInput.value));

  // If the offer to switch to a recurring donation is enabled, clicking one of the amount buttons should not
  // immediately take the user to the next step.
  if (props.recurringDonationOfferEnabled) {
    // If the segmented control is used and the one-time option is not preselected, the user has to click one of the
    // two buttons. This click "commits" the step, so the 'Continue' doesn't have to be shown.
    if (props.recurringDonationOfferUiElement === 'segmented_control'
      && !props.recurringDonationOfferOnetimeButtonPreselected) {
      amountCommitButtonVisible.value = false;
    } else {
      amountCommitButtonVisible.value = true;
    }
  } else {
    amountCommitButtonVisible.value = true;
  }

  emit('validate');
}

function chooseRecurringDonationOffer() {
  if (amountCommitButtonVisible.value === false) {
    emit('commit');
  }
}
</script>

<template>
  <div class="amount-step">
    <StepItem
      :number="1"
      :opened="store.amountStepOpen"
      :title="amountStepTitle"
      tls-notice="Die Übermittlung Deiner Daten erfolgt sicher via SSL-Verschlüsselung."
      @try-open="emit('try-open')"
    >
      <template #opened>
        <slot />

        <div
          class="amount-step__amounts"
          :class="`amount-step__button-count-${amountChoices.length}`"
        >
          <div
            v-for="(element, index) in amountChoices"
            :key="element"
            class="amount-step__choice-wrapper"
          >
            <cmpr-button
              class="amount-step__choice"
              :data-amount="element"
              data-analytics-context="amount-step"
              :data-analytics-id="`${analyticsIdPrefix}-form-amount-button-${index + 1}`"
              :data-analytics-inner-text="element"
              size="medium"
              :variant="amountChoiceVariant(index)"
              @click.prevent="chooseAmount(element)"
            >
              {{ localizeAmount(element) }}
            </cmpr-button>

            <div
              v-if="amountChoice === element"
              class="amount-step__check"
            >
              <cmpr-icon-solid-check />
            </div>
          </div>

          <div class="amount-step__input-wrapper">
            <CmprInput
              v-model="amountInput"
              class="amount-step__input"
              :class="{'is-invalid': store.amountErrorUnlocked && store.errors.amount}"
              data-analytics-context="amount-step"
              :data-analytics-id="`${analyticsIdPrefix}-form-amount-field`"
              data-analytics-inner-text="amount"
              inputmode="decimal"
              placeholder="Betrag"
              @input="inputAmount"
              @keydown.enter.prevent="emit('commit')"
            />

            <div
              v-if="amountInput"
              class="amount-step__check"
            >
              <cmpr-icon-solid-check />
            </div>
          </div>
        </div>

        <cmpr-form-hint
          v-if="store.amountErrorUnlocked && store.errors.amount"
          variant="error"
        >
          {{ store.errors.amount }}
        </cmpr-form-hint>

        <recurring-donation-offer
          v-if="props.recurringDonationOfferEnabled"
          :analytics-id-prefix="analyticsIdPrefix"
          :recurring-donation-offer-checkbox-label="recurringDonationOfferCheckboxLabel"
          :recurring-donation-offer-hint-placement="recurringDonationOfferHintPlacement"
          :recurring-donation-offer-hint-text="recurringDonationOfferHintText"
          :recurring-donation-offer-onetime-button-label="recurringDonationOfferOnetimeButtonLabel"
          :recurring-donation-offer-onetime-button-preselected="recurringDonationOfferOnetimeButtonPreselected"
          :recurring-donation-offer-recurring-button-label="recurringDonationOfferRecurringButtonLabel"
          :recurring-donation-offer-request-text="recurringDonationOfferRequestText"
          :recurring-donation-offer-ui-element="recurringDonationOfferUiElement"
          @decide-on-recurring-donation-offer="chooseRecurringDonationOffer"
        />

        <cmpr-button
          v-if="amountCommitButtonVisible"
          class="w-100 mt-60"
          data-analytics-context="amount-step"
          :data-analytics-id="`${analyticsIdPrefix}-form-amount-commit-button`"
          size="small"
          variant="secondary"
          @click.prevent="emit('commit')"
        >
          Weiter
        </cmpr-button>
      </template>

      <template #closed>
        <cmpr-prefilled-area
          class="amount-step__summary"
          editable="editable"
          variant="success"
        >
          <div class="fw-bold">
            {{ summary(store.values.amount) }}
          </div>
        </cmpr-prefilled-area>
      </template>
    </StepItem>
  </div>
</template>

<style scoped lang="scss">
@import '@cta/frontend/stylesheets/custom-media';

.amount-step {
  --check-background-color: var(--cmpr-color-success);
  --check-border-color: var(--cmpr-color-neutral-20);
  --check-border-width: 3px;
  --check-color: var(--cmpr-color-white);
  --check-font-size: var(--cmpr-font-size-10);
  --check-font-weigth: var(--cmpr-font-weight-30);
  --check-size: 28px;
  --amounts-grid-template-columns: repeat(3, minmax(0, 1fr));
  --amount-input-column-span-with-3-buttons: 3;
  --amount-input-column-span-with-4-buttons: 2;
  --amount-input-column-span-with-6-buttons: 3;
  --amount-input-column-span-with-7-buttons: 2;

  @media (--cmpr-breakpoint-xs-min) {
    --amounts-grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .amount-step__button-count-3 {
    @media (--cmpr-breakpoint-desktop-min) {
      --amounts-grid-template-columns: repeat(4, minmax(0, 1fr));
      --amount-input-column-span-with-3-buttons: 1;
    }
  }

  .amount-step__button-count-4 {
    @media (--cmpr-breakpoint-desktop-min) {
      --amounts-grid-template-columns: repeat(5, minmax(0, 1fr));
      --amount-input-column-span-with-3-buttons: 1;
      --amount-input-column-span-with-4-buttons: 1;
    }
  }
}

.amount-step__amounts {
  display: grid;
  gap: 16px;
  grid-auto-rows: 1fr;
  grid-template-columns: var(--amounts-grid-template-columns);
}

.amount-step__choice-wrapper,
.amount-step__input-wrapper {
  display: grid;
  grid-auto-rows: 1fr;
  grid-template-areas: 'wrapper';
  height: 100%;
  width: 100%;

  > * {
    grid-area: wrapper;
    justify-self: stretch;
  }

  .amount-step__choice {
    --cmpr-button-padding-inline: var(--cmpr-space-0);
  }
}

.amount-step__button-count-3 > .amount-step__input-wrapper {
  grid-column: span var(--amount-input-column-span-with-3-buttons);
}

.amount-step__button-count-4 > .amount-step__input-wrapper {
  grid-column: span var(--amount-input-column-span-with-4-buttons);
}

.amount-step__button-count-6 > .amount-step__input-wrapper {
  grid-column: span var(--amount-input-column-span-with-6-buttons);
}

.amount-step__button-count-7 > .amount-step__input-wrapper {
  grid-column: span var(--amount-input-column-span-with-7-buttons);
}

.amount-step__check {
  background-color: var(--check-background-color);
  border: var(--check-border-width) solid var(--check-border-color);
  border-radius: 50%;
  bottom: calc(var(--check-size) / -5 * 2);
  box-sizing: border-box;
  color: var(--check-color);
  font-size: var(--check-font-size);
  font-weight: var(--check-font-weigth);
  height: var(--check-size);
  line-height: calc(var(--check-size) - 2 * var(--check-border-width));
  place-self: end;
  position: relative;
  right: calc(var(--check-size) / -5 * 2);
  text-align: center;
  width: var(--check-size);
  z-index: 2;
}
</style>
